import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
    useRef
} from 'react';

// Material UI components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

// Serendipity components
import {
    GlucoseGraph,
    BGCard,
    DataSourceSelector,
    ServerStatusHeader
} from 'components/Serendipity';

import { useInterval } from 'hooks/useIntervalHook';

// Auth Context
import { AuthContext } from 'lib/Auth';

// Libs
import { getNightscoutEntries } from 'lib/nightscout';
import { DexcomClient } from 'lib/dexcom';
import { AbbottClient } from 'lib/abbott';

// Firebase
import { firebase, db } from '../../services/firebase';
import { getDatabase, ref, onValue } from 'firebase/database';
import {
    onSnapshot,
    query,
    collection,
    doc,
    orderBy,
    where
} from 'firebase/firestore';

const database = getDatabase(firebase);

export default function Dashboard() {
    const { userPreferences } = useContext(AuthContext);
    const { nightscout, abbott, uid, serendipity } = userPreferences;

    const abbottClient = new AbbottClient(abbott);
    const dexcomClient = new DexcomClient(nightscout);

    const { useV2Features } = serendipity;
    const { BG_TARGET_TOP, BG_TARGET_BOTTOM } = nightscout;

    const [preferences, setPreferences] = useState(userPreferences);

    const DEFAULT_ENTRY = {
        x: new Date(),
        y: 0,
        raw: {
            Value: 0,
            Trend: 'FLAT'
        }
    };

    const DEFAULT_CHART_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

    // Set up initial state for the dashboard.
    const [entries, setEntries] = useState([DEFAULT_ENTRY]);

    const DEFAULT_REFRESH_INTERVAL = 5 * 60 * 1000;

    const [fetchingEntries, setFetchingEntries] = useState(true);

    const [latestEntry, setLatestEntry] = useState(DEFAULT_ENTRY);

    const [priorEntry, setPriorEntry] = useState(DEFAULT_ENTRY);

    const [lastFetchTime, setLastFetchTime] = useState('Updating...');

    const [chartDataSource, setChartDataSource] = useState('NS');

    const firestoreUnsubscribeRef = useRef(null);
    const hasSubscribedSy = useRef(false);

    const getEntries = useCallback(() => {
        let unsubscribe;

        const handleLatestEntries = (entryList) => {
            const lastIndex = entryList.length - 1;
            setLatestEntry(
                entryList[lastIndex]?.data?.data || entryList[lastIndex]
            );
            setPriorEntry(
                entryList[lastIndex - 1]?.data?.data || entryList[lastIndex - 1]
            );
        };

        const fetchNightscout = async () => {
            const opts = {
                nsApiUrl: nightscout.base_url,
                queryStartTime: Date.now() - 24 * 60 * 60 * 1000,
                count: 100
            };
            const nsEntries = await getNightscoutEntries(opts);
            const formatted = nsEntries?.formattedEntries || [];

            if (formatted.length > 0) {
                setEntries(formatted);
                handleLatestEntries(formatted);
                setLastFetchTime(
                    new Date(
                        Date.now() + DEFAULT_CHART_REFRESH_INTERVAL
                    ).toLocaleTimeString()
                );
            }
        };

        const fetchDexcom = async () => {
            const dxEntries = await dexcomClient.getEntries();
            setEntries(dxEntries);
            setLatestEntry(dexcomClient.latestEntry);
            setPriorEntry(dexcomClient.priorEntry);
            setLastFetchTime(
                new Date(
                    Date.now() + DEFAULT_CHART_REFRESH_INTERVAL
                ).toLocaleTimeString()
            );
        };

        const fetchAbbott = async () => {
            const abEntries = await abbottClient.getEntries();
            setEntries(abEntries);
            setLatestEntry(abbottClient.latestEntry);
            setPriorEntry(abbottClient.priorEntry);
            setLastFetchTime(
                new Date(
                    Date.now() + DEFAULT_CHART_REFRESH_INTERVAL
                ).toLocaleTimeString()
            );
        };

        const subscribeSy = () => {
            if (hasSubscribedSy.current) {
                // Already subscribed to SY
                return;
            }

            hasSubscribedSy.current = true;

            const entriesRef = collection(doc(db, 'users', uid), 'entries');
            const threeHoursAgo = new Date(Date.now() - 3 * 60 * 60 * 1000);
            const entriesQuery = query(
                entriesRef,
                where('data.deviceCreateAt', '>=', threeHoursAgo),
                orderBy('data.deviceCreateAt', 'asc')
            );

            unsubscribe = onSnapshot(
                entriesQuery,
                (querySnapshot) => {
                    setFetchingEntries(false);
                    if (!querySnapshot.empty) {
                        const newEntries = querySnapshot.docs.map((doc) => ({
                            id: doc.id,
                            raw: doc.data().data,
                            x: doc.data().data.deviceCreateAt.toDate(),
                            y: doc.data().data.glucoseValue,
                            glucoseValue: doc.data().data.glucoseValue,
                            readingDirection: doc.data().data.readingDirection
                        }));
                        setEntries(newEntries);
                        handleLatestEntries(newEntries);
                        setLastFetchTime(
                            new Date(
                                Date.now() + DEFAULT_CHART_REFRESH_INTERVAL
                            ).toLocaleTimeString()
                        );
                    }
                },
                (error) => {
                    console.error('Error getting SY entries:', error);
                }
            );

            firestoreUnsubscribeRef.current = () => {
                unsubscribe();
                hasSubscribedSy.current = false;
            };

            setTimeout(() => {
                if (firestoreUnsubscribeRef.current) {
                    firestoreUnsubscribeRef.current();
                    setFetchingEntries(false);
                }
            }, DEFAULT_CHART_REFRESH_INTERVAL);
        };

        const run = async () => {
            setFetchingEntries(true);

            try {
                switch (chartDataSource) {
                    case 'NS':
                        await fetchNightscout();
                        break;
                    case 'DX':
                        await fetchDexcom();
                        break;
                    case 'AB':
                        await fetchAbbott();
                        break;
                    case 'SY':
                        subscribeSy();
                        break;
                    default:
                        console.warn(`Unknown data source: ${chartDataSource}`);
                        break;
                }
            } catch (err) {
                console.error('Error fetching entries:', err);
            } finally {
                // Don't override Firestore logic
                if (chartDataSource !== 'SY') {
                    setFetchingEntries(false);
                }
            }
        };

        return run();
    }, [chartDataSource]);

    useInterval(async () => {
        getEntries();
    }, DEFAULT_REFRESH_INTERVAL);

    useEffect(() => {
        getEntries();
    }, [getEntries]);

    useEffect(() => {
        const updatePreferences = async () => {
            const userPrefRef = ref(database, 'preferences/' + uid);

            onValue(userPrefRef, (snapshot) => {
                const userPreferences = snapshot.val();
                setPreferences(userPreferences);
            });
        };

        updatePreferences();
    }, [uid]);

    useEffect(() => {
        return () => {
            firestoreUnsubscribeRef.current?.();
        };
    }, []);

    const handleDataSourceSelect = (event) => {
        const { value } = event.target;
        // Clear entries whenever a data source is selected.
        setFetchingEntries(true);
        setEntries([DEFAULT_ENTRY]);
        setLatestEntry(DEFAULT_ENTRY);
        setPriorEntry(DEFAULT_ENTRY);
        setChartDataSource(value);
    };

    return (
        <div>
            <GridContainer>
                <GridItem xs={12} sm={12} md={12} lg={12}>
                    <GridContainer>
                        <GridItem xs={12} sm={12} md={3}>
                            <DataSourceSelector
                                handleDataSourceSelect={handleDataSourceSelect}
                                chartDataSource={chartDataSource}
                                useAdvancedFeatures={useV2Features}
                            />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={3}>
                            <ServerStatusHeader userPreferences={preferences} />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem xs={12} sm={12} md={12} lg={12}>
                    <GridContainer>
                        <GridItem xs={12} sm={12} md={4} lg={3}>
                            <BGCard
                                loading={fetchingEntries}
                                entries={entries}
                                latestEntry={latestEntry}
                                priorEntry={priorEntry}
                                updateTime={lastFetchTime}
                                targetHigh={BG_TARGET_TOP}
                                targetLow={BG_TARGET_BOTTOM}
                            />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={12} lg={8}>
                            <GlucoseGraph
                                data={entries}
                                loading={fetchingEntries}
                                targetHigh={BG_TARGET_TOP}
                                targetLow={BG_TARGET_BOTTOM}
                                handleDataSourceSelect={handleDataSourceSelect}
                                chartDataSource={chartDataSource}
                            />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </div>
    );
}
